import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdmin } from '../AdminContext'
import { clone, uniqueSlug } from '../editing'
import { emptyPost } from './socialRules'
import type { SocialPost } from '../../core/types/social'

/** Posts aanmaken, wijzigen en verwijderen in het concept van het beheer. */
export function useSocialPosts() {
  const { marketing, setMarketing } = useAdmin()
  const navigate = useNavigate()

  const newId = useCallback(
    () => uniqueSlug(`post-${Date.now().toString(36)}`, marketing.posts.map((p) => p.id)),
    [marketing.posts],
  )

  /** Maakt een post en opent hem direct. */
  const createPost = useCallback(
    (patch: Partial<SocialPost> = {}) => {
      const post = emptyPost({ ...patch, id: newId() })
      setMarketing((m) => ({ ...m, posts: [...m.posts, post] }))
      navigate(`/be/social/${post.id}`)
      return post.id
    },
    [newId, setMarketing, navigate],
  )

  const updatePost = useCallback(
    (id: string, patch: Partial<SocialPost>) =>
      setMarketing((m) => ({ ...m, posts: m.posts.map((p) => (p.id === id ? { ...p, ...patch } : p)) })),
    [setMarketing],
  )

  /** Haalt de post weg en geeft een functie die precies die post op zijn oude plek terugzet. */
  const removePost = useCallback(
    (id: string) => {
      const at = marketing.posts.findIndex((p) => p.id === id)
      const removed = marketing.posts[at]
      setMarketing((m) => ({ ...m, posts: m.posts.filter((p) => p.id !== id) }))
      return () => {
        if (!removed) return
        setMarketing((m) => {
          if (m.posts.some((p) => p.id === id)) return m
          const posts = [...m.posts]
          posts.splice(Math.min(at, posts.length), 0, removed)
          return { ...m, posts }
        })
      }
    },
    [marketing.posts, setMarketing],
  )

  const duplicatePost = useCallback(
    (post: SocialPost) => createPost({ ...clone(post), title: `${post.title} (kopie)`, status: 'concept', date: '' }),
    [createPost],
  )

  return { posts: marketing.posts, createPost, updatePost, removePost, duplicatePost }
}
